/**
 * System / Devices — admin observability for the mobile rollout
 * (ADR-021 follow-up).
 *
 * Reads /v1/system/devices and answers the support-tier question
 * every admin asks once a mobile binary lands: "Who has the app?
 * On what platforms? When did they last open it? How many tokens
 * have been auto-revoked?"
 *
 * Page chrome follows the §13 standard:
 *   - 42px / 12px page padding
 *   - 1px-bordered container, no shadow
 *   - Inline stats line (NOT 4-card top row)
 *   - Hover changes border colour only
 *
 * Filter: include_revoked toggle in the header. Default-on so the
 * admin sees the auto-revocation pipeline (ADR-021 D4) at work.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { BlockOutlined } from '@mui/icons-material';

import { systemApi } from '../../api';
import { activeLocale } from '../../utils/datetime';
import type {
  SystemDevicesSummary,
  SystemWorkerDevice,
} from '../../api/system';

const Devices: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<SystemDevicesSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeRevoked, setIncludeRevoked] = useState(true);

  // Force-revoke dialog state
  const [revokeTarget, setRevokeTarget] = useState<SystemWorkerDevice | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await systemApi.systemDevicesApi.list(includeRevoked));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('common:feedback.loadFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [includeRevoked, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await systemApi.systemDevicesApi.revoke(revokeTarget.id);
      setToast(t('system:devices.toastRevoked', { name: revokeTarget.worker_name ?? revokeTarget.id }));
      setRevokeTarget(null);
      await load();
    } catch (e) {
      const detail = e instanceof Error ? e.message : t('system:devices.revokeFailed');
      setToast(detail);
    } finally {
      setRevoking(false);
    }
  };

  const fmtTime = useMemo(
    () => (iso: string | null | undefined): string => {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleString(activeLocale());
      } catch {
        return iso;
      }
    },
    [],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, fontSize: '1.125rem' }}
        >
          {t('system:devices.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('system:devices.subtitle')}
        </Typography>
      </Box>

      {/* Inline stats (§13). Avoid 4-card top strip. */}
      {data ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          <strong>{data.total}</strong> {t('system:devices.statsDevices')} ·{' '}
          <strong>{data.active}</strong> {t('system:devices.statsActive')} ·{' '}
          <strong>{data.revoked}</strong> {t('system:devices.statsRevoked')} ·{' '}
          <strong>{data.ios}</strong> iOS · <strong>{data.android}</strong>{' '}
          Android
          {data.web > 0 ? (
            <>
              {' · '}
              <strong>{data.web}</strong> web
            </>
          ) : null}
        </Typography>
      ) : null}

      {/* Filter row */}
      <Box sx={{ width: '100%', mb: 1.5 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeRevoked}
              onChange={(_, v) => setIncludeRevoked(v)}
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              {t('system:devices.includeRevoked')}
            </Typography>
          }
        />
      </Box>

      {error ? (
        <Alert severity="error" variant="outlined" sx={{ mb: 1.5, width: '100%' }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !data || data.devices.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: 5,
            textAlign: 'center',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('system:devices.noDevices')}
          </Typography>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {t('system:devices.noDevicesHint')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 'none',
          }}
        >
          <Table size="small">
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600, fontSize: '0.75rem' },
              }}
            >
              <TableRow>
                <TableCell>{t('system:devices.colWorker', { defaultValue: 'Worker' })}</TableCell>
                <TableCell>{t('system:devices.colPlatform', { defaultValue: 'Platform' })}</TableCell>
                <TableCell>{t('system:devices.colDevice', { defaultValue: 'Device' })}</TableCell>
                <TableCell>{t('system:devices.colApp', { defaultValue: 'App' })}</TableCell>
                <TableCell>{t('system:devices.colLastActive', { defaultValue: 'Last active' })}</TableCell>
                <TableCell>{t('system:devices.colRegistered', { defaultValue: 'Registered' })}</TableCell>
                <TableCell align="center">{t('system:devices.colStatus', { defaultValue: 'Status' })}</TableCell>
                <TableCell align="right">{t('system:devices.colActions', { defaultValue: 'Actions' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.devices.map((d) => (
                <DeviceRow
                  key={d.id}
                  d={d}
                  fmtTime={fmtTime}
                  onRevoke={() => setRevokeTarget(d)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Force-revoke confirm */}
      <Dialog
        open={!!revokeTarget}
        onClose={() => !revoking && setRevokeTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            Revoke device?
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            This stops push notifications immediately for{' '}
            <strong>
              {revokeTarget?.worker_name ?? revokeTarget?.worker_email ?? revokeTarget?.id}
            </strong>
            's <strong>{revokeTarget?.platform}</strong> device. The row stays
            in the audit trail. The worker can re-register on the next
            mobile sign-in.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => setRevokeTarget(null)}
            disabled={revoking}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRevoke}
            disabled={revoking}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {revoking ? <CircularProgress size={18} /> : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const DeviceRow: React.FC<{
  d: SystemWorkerDevice;
  fmtTime: (iso: string | null | undefined) => string;
  onRevoke: () => void;
}> = ({ d, fmtTime, onRevoke }) => {
  const { t } = useTranslation();
  const platformChip =
    d.platform === 'ios'
      ? { label: 'iOS', color: 'primary' as const }
      : d.platform === 'android'
      ? { label: 'Android', color: 'success' as const }
      : { label: d.platform, color: 'default' as const };
  const isRevoked = d.revoked_at != null;
  return (
    <TableRow hover sx={{ opacity: isRevoked ? 0.7 : 1 }}>
      <TableCell>
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {d.worker_name ?? '—'}
          </Typography>
          {d.worker_email ? (
            <Typography variant="caption" color="text.secondary">
              {d.worker_email}
            </Typography>
          ) : null}
        </Stack>
      </TableCell>
      <TableCell>
        <Chip
          label={platformChip.label}
          color={platformChip.color}
          size="small"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      </TableCell>
      <TableCell>
        <Stack spacing={0.25} alignItems="flex-start">
          <Typography
            variant="caption"
            color={d.device_id ? 'text.primary' : 'text.disabled'}
            sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
          >
            {d.device_id ?? '—'}
          </Typography>
          {/* WHICH PHONE MAY ACTUALLY CLOCK IN (2026-09-08). This page lists PUSH registrations, while
              punching is authorised by a separate table — so a row could read `Active` here while the app
              refused the punch, which is what was reported. Shown only on the device that holds the slot,
              so the column stays quiet on the common case. */}
          {d.punch_slot ? (
            <Chip
              label={t('system:devices.clockInDevice')}
              color="info"
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem', borderRadius: 1 }}
            />
          ) : null}
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {d.app_version ?? '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">{fmtTime(d.last_active_at)}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {fmtTime(d.registered_at)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        {isRevoked ? (
          <Chip
            label="Revoked"
            size="small"
            variant="outlined"
            color="error"
            sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }}
          />
        ) : (
          <Chip
            label="Active"
            size="small"
            variant="outlined"
            color="success"
            sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }}
          />
        )}
      </TableCell>
      <TableCell align="right">
        {isRevoked ? null : (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<BlockOutlined sx={{ fontSize: 14 }} />}
            onClick={onRevoke}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.75rem', py: 0 }}
          >
            Revoke
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

export default Devices;
